import type { Bounds, OverlayElement } from '@/types';

export interface AlignmentGuide {
    type: 'vertical' | 'horizontal';
    position: number; // Pixel position
    label?: string;
}

export interface SnapResult {
    x: number;
    y: number;
    guides: AlignmentGuide[];
}

const SNAP_THRESHOLD = 8; // Pixels - how close before snapping

/**
 * Calculate alignment guides and snap positions for an overlay being moved
 */
export function calculateSnapAndGuides(
    movingBounds: Bounds,
    videoWidth: number,
    videoHeight: number,
    otherOverlays: OverlayElement[],
    scale: number
): SnapResult {
    const guides: AlignmentGuide[] = [];
    let snapX = movingBounds.x;
    let snapY = movingBounds.y;

    // Get key positions of the moving overlay
    const movingLeft = movingBounds.x;
    const movingRight = movingBounds.x + movingBounds.width;
    const movingCenterX = movingBounds.x + movingBounds.width / 2;
    const movingTop = movingBounds.y;
    const movingBottom = movingBounds.y + movingBounds.height;
    const movingCenterY = movingBounds.y + movingBounds.height / 2;

    // Video center points
    const videoCenterX = videoWidth / 2;
    const videoCenterY = videoHeight / 2;

    // Check snapping to video center (horizontal)
    if (Math.abs(movingCenterX - videoCenterX) < SNAP_THRESHOLD) {
        snapX = videoCenterX - movingBounds.width / 2;
        guides.push({
            type: 'vertical',
            position: videoCenterX * scale,
            label: 'Center'
        });
    }

    // Check snapping to video center (vertical)
    if (Math.abs(movingCenterY - videoCenterY) < SNAP_THRESHOLD) {
        snapY = videoCenterY - movingBounds.height / 2;
        guides.push({
            type: 'horizontal',
            position: videoCenterY * scale,
            label: 'Center'
        });
    }

    // Check snapping to video edges
    const edgeMargin = 20; // Standard margin from edge

    // Left edge
    if (Math.abs(movingLeft - edgeMargin) < SNAP_THRESHOLD) {
        snapX = edgeMargin;
        guides.push({ type: 'vertical', position: edgeMargin * scale });
    } else if (Math.abs(movingLeft) < SNAP_THRESHOLD) {
        snapX = 0;
        guides.push({ type: 'vertical', position: 0 });
    }

    // Right edge
    if (Math.abs(movingRight - (videoWidth - edgeMargin)) < SNAP_THRESHOLD) {
        snapX = videoWidth - edgeMargin - movingBounds.width;
        guides.push({ type: 'vertical', position: (videoWidth - edgeMargin) * scale });
    } else if (Math.abs(movingRight - videoWidth) < SNAP_THRESHOLD) {
        snapX = videoWidth - movingBounds.width;
        guides.push({ type: 'vertical', position: videoWidth * scale });
    }

    // Top edge
    if (Math.abs(movingTop - edgeMargin) < SNAP_THRESHOLD) {
        snapY = edgeMargin;
        guides.push({ type: 'horizontal', position: edgeMargin * scale });
    } else if (Math.abs(movingTop) < SNAP_THRESHOLD) {
        snapY = 0;
        guides.push({ type: 'horizontal', position: 0 });
    }

    // Bottom edge
    if (Math.abs(movingBottom - (videoHeight - edgeMargin)) < SNAP_THRESHOLD) {
        snapY = videoHeight - edgeMargin - movingBounds.height;
        guides.push({ type: 'horizontal', position: (videoHeight - edgeMargin) * scale });
    } else if (Math.abs(movingBottom - videoHeight) < SNAP_THRESHOLD) {
        snapY = videoHeight - movingBounds.height;
        guides.push({ type: 'horizontal', position: videoHeight * scale });
    }

    // Check snapping to other overlays
    for (const other of otherOverlays) {
        const otherLeft = other.bounds.x;
        const otherRight = other.bounds.x + other.bounds.width;
        const otherCenterX = other.bounds.x + other.bounds.width / 2;
        const otherTop = other.bounds.y;
        const otherBottom = other.bounds.y + other.bounds.height;
        const otherCenterY = other.bounds.y + other.bounds.height / 2;

        // Align left edges
        if (Math.abs(movingLeft - otherLeft) < SNAP_THRESHOLD) {
            snapX = otherLeft;
            guides.push({ type: 'vertical', position: otherLeft * scale });
        }

        // Align right edges
        if (Math.abs(movingRight - otherRight) < SNAP_THRESHOLD) {
            snapX = otherRight - movingBounds.width;
            guides.push({ type: 'vertical', position: otherRight * scale });
        }

        // Align centers X
        if (Math.abs(movingCenterX - otherCenterX) < SNAP_THRESHOLD) {
            snapX = otherCenterX - movingBounds.width / 2;
            guides.push({ type: 'vertical', position: otherCenterX * scale });
        }

        // Align top edges
        if (Math.abs(movingTop - otherTop) < SNAP_THRESHOLD) {
            snapY = otherTop;
            guides.push({ type: 'horizontal', position: otherTop * scale });
        }

        // Align bottom edges
        if (Math.abs(movingBottom - otherBottom) < SNAP_THRESHOLD) {
            snapY = otherBottom - movingBounds.height;
            guides.push({ type: 'horizontal', position: otherBottom * scale });
        }

        // Align centers Y
        if (Math.abs(movingCenterY - otherCenterY) < SNAP_THRESHOLD) {
            snapY = otherCenterY - movingBounds.height / 2;
            guides.push({ type: 'horizontal', position: otherCenterY * scale });
        }
    }

    return {
        x: snapX,
        y: snapY,
        guides
    };
}

/**
 * Draw alignment guides on canvas
 */
export function drawAlignmentGuides(
    ctx: CanvasRenderingContext2D,
    guides: AlignmentGuide[],
    canvasWidth: number,
    canvasHeight: number
): void {
    if (guides.length === 0) return;

    ctx.save();

    // Style for guides
    ctx.strokeStyle = '#ff6b6b';
    ctx.lineWidth = 1;
    ctx.setLineDash([4, 4]);

    guides.forEach((guide) => {
        ctx.beginPath();

        if (guide.type === 'vertical') {
            ctx.moveTo(guide.position, 0);
            ctx.lineTo(guide.position, canvasHeight);
        } else {
            ctx.moveTo(0, guide.position);
            ctx.lineTo(canvasWidth, guide.position);
        }

        ctx.stroke();

        // Draw label if present
        if (guide.label) {
            ctx.font = '10px sans-serif';
            ctx.fillStyle = '#ff6b6b';
            ctx.setLineDash([]);

            if (guide.type === 'vertical') {
                ctx.fillText(guide.label, guide.position + 4, 14);
            } else {
                ctx.fillText(guide.label, 4, guide.position - 4);
            }
        }
    });

    ctx.restore();
}

/**
 * Quick position presets for overlays
 */
export type PositionPreset = 
    | 'top-left' | 'top-center' | 'top-right'
    | 'center-left' | 'center' | 'center-right'
    | 'bottom-left' | 'bottom-center' | 'bottom-right';

/**
 * Calculate bounds for a position preset
 */
export function getPresetBounds(
    preset: PositionPreset,
    overlayWidth: number,
    overlayHeight: number,
    videoWidth: number,
    videoHeight: number,
    margin: number = 20
): Bounds {
    let x = 0;
    let y = 0;

    // Calculate X position
    if (preset.includes('left')) {
        x = margin;
    } else if (preset.includes('right')) {
        x = videoWidth - overlayWidth - margin;
    } else {
        // center
        x = (videoWidth - overlayWidth) / 2;
    }

    // Calculate Y position
    if (preset.includes('top')) {
        y = margin;
    } else if (preset.includes('bottom')) {
        y = videoHeight - overlayHeight - margin;
    } else {
        // center
        y = (videoHeight - overlayHeight) / 2;
    }

    return {
        x,
        y,
        width: overlayWidth,
        height: overlayHeight
    };
}

